import { EducationResponse, EmploymentResponse, FeedbackResponse, InterviewResponse, JobAnalysisApiResponse, JobResponse, PitchAnalysisResponse, QuestionsPayload } from '@/lib/api';

// Demo Job
export const demoJob: JobResponse = {
  id: 'demo-job-1',
  title: 'Senior Product Manager',
  company: 'Acme Corp',
  description: 'Lead product strategy and cross-functional execution to drive customer outcomes and business growth.',
  location: 'Remote',
  remote: 'yes',
  salary: '$150k - $180k',
  generation_status: 'ready',
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
};

// Demo Employment and Education data
export const demoEmployment: EmploymentResponse[] = [
  {
    id: 'emp-1',
    user_id: 'demo-user',
    company: 'TechCorp',
    location: 'San Francisco, CA',
    title: 'Senior Product Manager',
    description: 'Led cross-functional teams to deliver 3 major product initiatives, driving 40% increase in user engagement and $2M ARR growth. Managed roadmap prioritization across 5 engineering teams.',
    start_date: '2021-01-01',
    end_date: null,
    is_ongoing: true,
    skills: [],
    details: [],
  },
  {
    id: 'emp-2',
    user_id: 'demo-user',
    company: 'StartupInc',
    location: 'Remote',
    title: 'Product Manager',
    description: 'Launched 2 new product features from concept to market, conducted user research with 200+ customers, and collaborated with engineering on technical feasibility and scope.',
    start_date: '2019-03-01',
    end_date: '2020-12-31',
    is_ongoing: false,
    skills: [],
    details: [],
  },
];

export const demoEducation: EducationResponse[] = [
  {
    id: 'edu-1',
    user_id: 'demo-user',
    institution: 'University of Studying Real Hard',
    degree_type: 'Bachelor of Science',
    major: 'Computer Science',
    start_date: '2015-09-01',
    end_date: '2019-05-31',
    is_ongoing: false,
  },
  {
    id: 'edu-2',
    user_id: 'demo-user',
    institution: 'Legit Online Institution',
    degree_type: 'Certification',
    major: 'Advanced React Development',
    start_date: '2020-06-01',
    end_date: '2020-08-31',
    is_ongoing: false,
  },
];

// Demo Questions
export const q1: QuestionsPayload = {
    Question: {
      id: 'q-1',
      question: 'Imagine our company is considering entering a new market with a lightweight version of our core product. How would you evaluate whether this is the right strategic move?',
      expected: 'The candidate should approach a new market opportunity with structure and rigor, starting by sizing the market using frameworks like TAM/SAM/SOM, validating demand through customer research, and analyzing the competitive landscape. They should balance quantitative data with qualitative insights, involve stakeholders early in the process, and make clear that the decision is about alignment with long-term company strategy and resources, not just chasing revenue.',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    },
    Type: 'strategic',
  };
  
  export const q2: QuestionsPayload = {
    Question: {
      id: 'q-2',
      question: 'You have a backlog of 50 feature requests from different stakeholders, but your engineering team can only commit to 3 features this quarter. How do you prioritize?',
      expected: 'The candidate should demonstrate a clear and disciplined prioritization approach, such as using frameworks like RICE or impact vs. effort, while keeping customer value and company OKRs central. They should show they are comfortable making trade-offs between short-term revenue and long-term product health, and be willing to say “no” in a way that is empathetic and data-backed. They should also emphasize transparent communication so stakeholders understand and support the prioritization decisions.',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    },
    Type: 'execution',
  };
  
  export const q3: QuestionsPayload = {
    Question: {
      id: 'q-3',
      question: 'Two weeks before launch, engineering tells you a key feature will be delayed by a month, but marketing has already planned campaigns around that feature. How do you handle this situation?',
      expected: 'The candidate should respond to a last-minute launch delay with calm, solution-oriented thinking, laying out potential paths such as reducing scope, delaying, or phasing the release, and weighing these against customer impact. They should quickly involve marketing, collaborate to adjust campaigns, and avoid placing blame on engineering, instead positioning themselves as the bridge that maintains trust and momentum. Finally, they should demonstrate accountability by addressing the root cause of the delay and ensuring processes are improved to avoid similar issues in the future.',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    },
    Type: 'behavioural',
  };
  

  export const demoInterview: InterviewResponse = {
    id: 'iv-2',
    user_id: 'demo-user',
    job_id: demoJob.id,
    status: 'COMPLETE',
    questions: [q1, q2, q3],
    overall_description:
      'The candidate performed reasonably well across the three questions, showing strength in market analysis and prioritization frameworks, but weaker performance under pressure in the crisis scenario. Their answers demonstrated familiarity with structured approaches like TAM/SAM/SOM and RICE, and they showed empathy when communicating prioritization trade-offs. However, their responses would benefit from deeper examples, clearer articulation of metrics, and a more systematic approach to handling last-minute challenges.',
    overall_score: 3.3,
    hiring_considerations: [
      'Shows strong use of frameworks such as TAM/SAM/SOM and RICE.',
      'Communicates empathy and transparency in prioritization trade-offs.',
      'Could be early-to-mid career, still developing crisis leadership confidence.',
      'May need coaching to strengthen structured communication under pressure.',
    ],
    pros: [
      'Clear market analysis and customer validation methods.',
      'Disciplined use of prioritization frameworks and alignment with OKRs.',
      'Empathetic communication style with stakeholders.',
    ],
    cons: [
      'Responses sometimes lacked specific metrics and measurable outcomes.',
      'Crisis response felt reactive and surface-level.',
      'Limited detail on stakeholder alignment after setbacks.',
    ],
    suggestions: [
      'Use the STAR method to add clarity and flow to answers.',
      'Provide concrete examples with metrics (e.g., revenue impact, usage growth).',
      'When discussing setbacks, outline multiple options and trade-offs.',
      'Highlight post-launch lessons and how they informed later decisions.',
    ],
    question_order: ['q-1', 'q-2', 'q-3'],
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };  
  
  export const demoFeedback: FeedbackResponse[] = [
    {
      id: 'f-1',
      question: 'q-1',
      answer: 'For the new market opportunity, I would start with TAM/SAM/SOM to size the potential, then conduct 8–10 customer interviews and a short survey to validate demand. I’d benchmark competitors on pricing and features, then run a lightweight prototype to test willingness to pay. I’d bring in sales and marketing early to ensure alignment, and only move forward if the opportunity aligns with our 3-year strategy and resource availability.',
      thought: 'Strong, structured response with good use of frameworks and research methods.',
      reasoning: 'Demonstrates strategic thinking, customer validation, and alignment with company vision. Could be stronger with examples of tools used and post-launch evaluation strategies.',
      grade: 3.9,
      feedback: 'Excellent answer overall. To improve, add more detail on how similar evaluations influenced past product decisions and describe how you would validate success after launch.',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    },
    {
      id: 'f-2',
      question: 'q-2',
      answer: 'I use RICE scoring to prioritize, focusing on features that deliver the highest customer impact relative to effort. I also check alignment with OKRs so we’re not just chasing requests. I share the roadmap and rationale with stakeholders so they understand why some items won’t make the cut this quarter.',
      thought: 'Solid and disciplined approach, but generic.',
      reasoning: 'Shows awareness of frameworks and alignment with company goals, but lacks real-world examples of outcomes or handling pushback from stakeholders.',
      grade: 3.4,
      feedback: 'Good answer. Strengthen it by including a concrete example where prioritization improved delivery or customer satisfaction, and describe how you handled conflicting pressures.',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    },
    {
      id: 'f-3',
      question: 'q-3',
      answer: 'If engineering told me a feature was delayed, I’d probably let marketing know and see if we could shift the campaign. If not, I’d push to get the feature done as quickly as possible so we don’t lose momentum.',
      thought: 'Surface-level and reactive rather than structured.',
      reasoning: 'Acknowledges the need for communication, but misses outlining structured options, trade-offs, customer impact, or process improvements.',
      grade: 2.7,
      feedback: 'Needs improvement. A stronger answer would outline multiple paths (reduced scope, delay, phased release), evaluate customer impact, and demonstrate proactive leadership in coordinating stakeholders.',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    },
  ];  
  

export const demoInterviewsList: InterviewResponse[] = [
  demoInterview,
  {
    ...demoInterview,
    id: 'iv-2',
    status: 'INCOMPLETE',
    overall_score: 0,
    created_at: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3).toISOString(),
    updated_at: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3).toISOString(),
  },
];

// Demo Pitch data
export const demoPitch: PitchAnalysisResponse = {
  id: 'pitch-1',
  user_id: 'demo-user',
  content:
    "Hi, I'm a product manager with 7+ years experience driving customer-focused solutions. I've led cross-functional teams, launched features that increased engagement by 40%, and I love turning complex business challenges into simple, impactful products.",
  time_limit: '60',
  thoughts: 'Strong structure and concise delivery; showcases measurable outcomes.',
  summary:
    'Clear, confident pitch highlighting impact, scope, and motivation. Well aligned to product management roles with leadership and business wins.',
  strengths: [
    'Quantifies impact (40% engagement improvement).',
    'Crisp articulation of scope and responsibilities.',
    'Positive, energetic tone with customer-focused framing.',
  ],
  areas_for_improvement: [
    'Add a brief future-focus line tailored to the target role.',
    'Consider one concrete example of stakeholder management.',
  ],
  coaching_tips: [
    'Use a past → present → future arc; end with what you want next.',
    'Name one framework or metric you own to make the story more tangible.',
    'Slow down slightly to emphasize the impact statement.',
  ],
  clarity_score: 3.6,
  structure_score: 3.5,
  relevance_score: 3.7,
  overall_score: 3.6,
  status: 'complete',
  pitch_type: 'employment',
  employment_id: 'emp-1',
  education_id: '',
  job_id: demoJob.id,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
};

// Alternative demo pitch targeting education
export const demoPitchEducation: PitchAnalysisResponse = {
  id: 'pitch-edu-1',
  user_id: 'demo-user',
  content:
    "I'm a Computer Science graduate with expertise in web development and HCI. My academic foundation combined with hands-on React certification allows me to build user-centered applications that balance technical excellence with great user experience.",
  time_limit: '60',
  thoughts: 'Academic focus with practical application; good balance of theory and skills.',
  summary:
    'Education-focused pitch that effectively bridges academic knowledge with practical skills. Shows thoughtful career planning and user-centered approach.',
  strengths: [
    'Clear educational foundation in relevant field.',
    'Connects academic learning to practical applications.',
    'Emphasizes user-centered design thinking.',
  ],
  areas_for_improvement: [
    'Add specific examples of projects or achievements.',
    'Quantify impact where possible (e.g., grades, project outcomes).',
  ],
  coaching_tips: [
    'Include one standout academic project or achievement.',
    'Connect theoretical knowledge to real-world problem solving.',
    'Show enthusiasm for continued learning and growth.',
  ],
  clarity_score: 3.4,
  structure_score: 3.3,
  relevance_score: 3.6,
  overall_score: 3.4,
  status: 'complete',
  pitch_type: 'education',
  employment_id: '',
  education_id: 'edu-1',
  job_id: demoJob.id,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
};

export const demoPitches: PitchAnalysisResponse[] = [
  demoPitch,
  {
    ...demoPitch,
    id: 'pitch-2',
    time_limit: '30',
    // Rougher demo pitch example (C-level) - Earlier attempt
    content:
      "hi… I'm a product manager, I guess I've been doing this for about seven years now. I've worked on some features and, um, helped with prioritization and made some engagement improvements — I think around 40%. I, uh, like trying to make complicated business problems a little easier to solve, though I'm not sure I always get it right",
    thoughts: 'Hesitant, filler words, and lacks structure; some signal but unclear narrative.',
    summary:
      'Overall, this pitch shows relevant experience but is undercut by hesitations, filler words, and weak structure. Impact is mentioned vaguely ("around 40%"), and there is no clear past → present → future arc. With more specific examples and a tighter delivery, this could improve quickly.',
    strengths: [
      'Mentions relevant experience (7 years product management)',
      'Shows awareness of impact metrics (40% improvements)',
      'Demonstrates business-focused mindset',
    ],
    areas_for_improvement: [
      'Eliminate filler words and hesitation',
      'Structure the pitch with clear beginning, middle, end',
      'Be more confident and specific about achievements',
      'Add concrete examples and future aspirations',
    ],
    coaching_tips: [
      'Practice your pitch out loud to reduce filler words',
      'Use the structure: Past achievement → Current role → Future goal',
      'Replace "I think" and "I guess" with confident statements',
      'Time yourself to stay within the 30-second limit',
    ],
    clarity_score: 2.2,
    structure_score: 2.0,
    relevance_score: 2.3,
    overall_score: 2.2,
    status: 'complete',
    created_at: new Date(Date.now() - 1000 * 60 * 60 * 6).toISOString(),
    updated_at: new Date(Date.now() - 1000 * 60 * 60 * 6).toISOString(),
  },
];

// Demo Job Analysis data
export const demoJobAnalysis: JobAnalysisApiResponse = {
  id: 'demo-analysis-1',
  job_id: demoJob.id,
  user_id: 'demo-user',
  description:
    'You have a strong background in product management with meaningful impact in cross-functional leadership, feature delivery, and business growth. This foundation positions you well for a Senior Product Manager role. To strengthen your candidacy, emphasize end-to-end ownership of product initiatives, data-driven decision making, and examples where you drove strategic alignment across engineering, design, and business stakeholders.',
  fit_rating: 'GREAT',
  competitive_advantage: [
    'Deep experience leading cross-functional teams with a focus on customer outcomes and business impact.',
    'Proven track record of delivering product initiatives that drove 40%+ engagement growth and $2M+ ARR.',
    'Strong ability to translate business strategy into actionable roadmaps and technical requirements.',
    'Experience with user research, prioritization frameworks, and stakeholder alignment.',
  ],
  areas_for_improvement: [
    'Make data analysis and metrics framework more explicit (experimentation, A/B testing, cohort analysis).',
    'Show examples of strategic product decisions (market expansion, competitive positioning, platform choices).',
    'Expand on leadership outcomes (team scaling, process improvements, stakeholder management at scale).',
  ],
  hiring_manager_thoughts: [
    '"Strong product leadership experience—how do they balance customer needs with business constraints?"',
    '"Where have they made tough prioritization decisions and managed competing stakeholder demands?"',
    '"Do they have experience with data-driven product development and experimentation frameworks?"',
    '"How have they influenced engineering velocity while maintaining product quality and user experience?"',
  ],
  recommendations: [
    'Tailor your resume to highlight end-to-end ownership of product initiatives and the strategic decisions you led.',
    'Quantify outcomes: engagement growth, revenue impact, user acquisition, retention improvements, and team productivity gains.',
    'Add a short "Product Strategy & Execution" section listing frameworks (RICE, OKRs), analytics tools, and cross-functional leadership experience.',
    'Include one STAR-formatted story showing how you drove alignment across product, engineering, and business to deliver a high-impact initiative.',
    'If applicable, mention process improvements (roadmap frameworks, experimentation processes, stakeholder communication) that scaled team effectiveness.',
  ],
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
};

// Demo Salary Negotiation data
export const demoNegotiation = {
  id: 'demo-negotiation-1',
  job_id: demoJob.id,
  scenario_type: 'employer_asks_your_range',
  listed_salary: '$150k - $180k',
  desired_salary: '$170k',
  messages: [
    {
      role: 'hiring_manager',
      content: "Hi! We're really excited about your candidacy. Before we move forward, I'd like to understand your salary expectations. What range are you looking for?",
      timestamp: new Date(Date.now() - 1000 * 60 * 10).toISOString(),
    },
    {
      role: 'user',
      content: "Thank you! I'm very interested in this role. Based on my research and experience level, I'm looking for something in the $170k-$180k range. I'm also open to discussing the full compensation package including equity and benefits.",
      timestamp: new Date(Date.now() - 1000 * 60 * 9).toISOString(),
    },
    {
      role: 'hiring_manager',
      content: "I appreciate you sharing that. Our budget for this role is around $165k. However, we do have a strong equity package and excellent benefits. Would you be open to discussing the total compensation value?",
      timestamp: new Date(Date.now() - 1000 * 60 * 8).toISOString(),
    },
    {
      role: 'user',
      content: "I understand. I'm definitely interested in learning more about the equity package and benefits. Could you share more details about the equity structure and what the benefits package includes? I want to make sure I'm evaluating the full opportunity.",
      timestamp: new Date(Date.now() - 1000 * 60 * 7).toISOString(),
    },
  ],
  overall_score: 4.2,
  strategy_score: 4.5,
  clarity_score: 4.3,
  relationship_score: 4.0,
  strengths: [
    'Demonstrated research and market awareness by citing specific range',
    'Showed flexibility while maintaining position',
    'Asked clarifying questions about total compensation',
    'Maintained professional and collaborative tone',
  ],
  areas_for_improvement: [
    'Could have anchored slightly higher initially to leave room for negotiation',
    'Might benefit from mentioning specific accomplishments that justify the range',
    'Consider asking about growth opportunities and promotion timeline',
  ],
  coaching_tips: [
    'Always research market rates before stating your range',
    'Frame your range based on value you bring, not just market data',
    'Ask about the full compensation package before making final decisions',
    'Maintain a collaborative tone - negotiation is a conversation, not a battle',
  ],
  summary: 'Your enthusiasm for the role came through clearly, which is a great way to start a negotiation. You also did well to highlight your experience and education. To improve, focus on backing up your salary expectations with market data and anchoring higher, and avoid revealing your target salary too early in the conversation.',
  thoughts: 'You demonstrated some preparation by mentioning your research and experience level, which shows thoughtfulness. You also expanded the conversation to other forms of compensation when the salary was not going as planned, which is a smart move. You could have improved by presenting hard data as to why you\'re worth the desired salary, and you probably revealed your target salary too early in the conversation. Overall, your strategy was okay, but could have been better with more preparation and a stronger anchor.',
  scenario_config: {
    personality: 'corporate_neutral',
  },
};

// Demo Learn/Article data
export const demoArticles = [
  {
    slug: 'getting-started-with-interviews',
    title: 'Getting Started with Interviews',
    category: 'Interview Preparation',
    description: 'Learn the fundamentals of interview preparation and how to approach your first interviews with confidence.',
    date: '2024-01-15',
  },
  {
    slug: 'salary-negotiation-basics',
    title: 'Salary Negotiation Basics',
    category: 'Career Development',
    description: 'Learn the fundamentals of salary negotiation and how to confidently discuss compensation during the hiring process.',
    date: '2024-01-25',
  },
  {
    slug: 'mastering-star-and-carl-methods',
    title: 'Mastering STAR and CARL Methods',
    category: 'Interview Preparation',
    description: 'Learn how to structure your answers using proven frameworks that help you communicate your experiences effectively.',
    date: '2024-02-01',
  },
  {
    slug: 'effective-salary-negotiation-strategies',
    title: 'Effective Salary Negotiation Strategies',
    category: 'Career Development',
    description: 'Advanced techniques for negotiating your salary and total compensation package.',
    date: '2024-02-10',
  },
  {
    slug: 'what-managers-really-look-for',
    title: 'What Managers Really Look For',
    category: 'Interview Preparation',
    description: 'Insights into what hiring managers are actually evaluating during interviews.',
    date: '2024-02-15',
  },
  {
    slug: 'resume-tips',
    title: 'Resume Tips That Get You Noticed',
    category: 'Career Development',
    description: 'Essential tips for crafting a resume that stands out to recruiters and hiring managers.',
    date: '2024-02-20',
  },
];

export const demoCategories = ['Interview Preparation', 'Career Development', 'Salary Negotiation'];
